'use client';

import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { createElement } from 'react';
import { cx } from '../utils/cx';
import { mergePresetClasses } from '../utils/mergePresetClasses';

export type ContainerPreset =
  | 'authNarrow'
  | 'authNarrowCenter'
  | 'authWide'
  | 'appNarrow'
  | 'appCentered'
  | 'appWide'
  | 'appFull'
  | 'prose'
  | 'dialog'
  | 'full'
  | 'appShell'
  | 'appShellCentered'
  | 'appShellWide'
  | 'appShellFill';

export type ContainerAs = 'div' | 'main' | 'section' | 'header' | 'footer' | 'nav' | 'aside' | 'article';

export type ContainerContext = 'standalone' | 'hero';

export interface ContainerProps extends HTMLAttributes<HTMLElement> {
  preset?: ContainerPreset;
  context?: ContainerContext;
  as?: ContainerAs;
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
}

// `appShell*` presets assert their own viewport height and are meant to be the
// outermost scroll owner. `appShellFill` is for a frame that already lives
// inside a sized (and usually safe-area padded) parent: it fills the parent box
// instead of re-declaring 100dvh, so a sticky footer/composer cannot be pushed
// below the visible bottom edge.
const APP_SHELL_FILL =
  'flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden [&>*]:min-h-0';

const PRESET_CLASS_STANDALONE: Record<ContainerPreset, string> = {
  authNarrow: 'mx-auto w-full max-w-[460px] px-4 py-10 sm:px-6 sm:py-12',

  authNarrowCenter:
    'mx-auto flex min-h-[100dvh] w-full max-w-[460px] flex-col justify-center px-4 py-10 ' +
    'sm:px-6 sm:py-14',

  authWide:
    'mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 lg:px-8',

  appNarrow: 'mx-auto w-full max-w-xl px-4 py-8 sm:px-6 lg:px-8',

  appCentered:
    'mx-auto flex min-h-[100dvh] w-full max-w-3xl flex-col items-center justify-center px-4 py-8 sm:px-6 lg:px-8',

  appWide: 'mx-auto w-full max-w-8xl px-4 py-8 sm:px-6 lg:pr-18 lg:pl-18',

  appFull: 'w-full',

  prose: 'mx-auto w-full max-w-prose px-4 py-8 sm:px-6',

  dialog:
    'mx-auto flex min-h-[100dvh] w-full max-w-sm flex-col items-center justify-center px-4 py-10 sm:px-6',

  full: 'w-full px-4 py-6 sm:px-6 lg:px-8',

  appShell: 'flex h-dvh min-h-0 w-full flex-col overflow-hidden [&>*]:min-h-0',
  appShellCentered:
    'mx-auto flex h-dvh min-h-0 w-full max-w-xl flex-col justify-center overflow-hidden px-4 [&>*]:min-h-0 sm:px-6',
  appShellWide: 'flex h-dvh min-h-0 w-full flex-col overflow-hidden [&>*]:min-h-0',
  appShellFill: APP_SHELL_FILL,
};

const PRESET_CLASS_HERO: Record<ContainerPreset, string> = {
  authNarrow: 'mx-auto w-full max-w-[460px] px-4 py-8 sm:px-6',
  authNarrowCenter: 'mx-auto w-full max-w-[460px] px-4 py-10 sm:px-6',
  authWide: 'mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 lg:px-8',
  appNarrow: 'mx-auto w-full max-w-xl px-4 py-8 sm:px-6 lg:px-8',
  appCentered: 'mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8',
  appWide: 'mx-auto w-full max-w-8xl px-4 py-8 sm:px-6 lg:pr-18 lg:pl-18',
  appFull: 'w-full',
  prose: 'mx-auto w-full max-w-prose px-4 py-8 sm:px-6',
  dialog: 'mx-auto w-full max-w-sm px-4 py-10 sm:px-6',
  full: 'w-full px-4 py-6 sm:px-6 lg:px-8',

  appShell: 'flex h-dvh min-h-0 w-full flex-col overflow-hidden [&>*]:min-h-0',
  appShellCentered:
    'mx-auto flex h-dvh min-h-0 w-full max-w-xl flex-col justify-center overflow-hidden px-4 [&>*]:min-h-0 sm:px-6',
  appShellWide: 'flex h-dvh min-h-0 w-full flex-col overflow-hidden [&>*]:min-h-0',
  appShellFill: APP_SHELL_FILL,
};

export function Container({
  preset = 'appWide',
  as = 'div',
  context = 'standalone',
  className,
  ariaLabel,
  children,
  ...rest
}: ContainerProps) {
  const classMap = context === 'hero' ? PRESET_CLASS_HERO : PRESET_CLASS_STANDALONE;
  const presetClass = classMap[preset] ?? classMap.appWide;

  const explicitAriaLabel = (rest as unknown as { ['aria-label']?: string })['aria-label'];
  const ariaLabelFinal = explicitAriaLabel ?? ariaLabel;

  // Preset utilities are the base layer; `className` is an explicit override.
  // Tailwind resolves conflicts by stylesheet order rather than attribute order,
  // so conflicting preset utilities (for example `h-dvh` vs an explicit
  // `h-full`) must be removed before rendering. This mirrors Box and Card.
  const mergedClassName = mergePresetClasses(presetClass, className);

  return createElement(
    as as ElementType,
    {
      ...rest,
      ...(ariaLabelFinal ? ({ 'aria-label': ariaLabelFinal } as const) : null),
      className: cx(mergedClassName),
    },
    children,
  );
}

Container.displayName = 'Container';